import { useEffect, useId, useRef, useState } from "react";
import favicon from "../assets/branding/artistnav-beta-icon.png";
import "./helloLandingBeta.css";
import { BETA_API, INDUSTRY_ROLES, INTERESTS, SHOWS_PER_MONTH } from "@/api/betaAccess";

/* Public beta application (artistnav.com/beta, QR entry point). Submitting stores an
 * application for owner review only: it never creates an account or an invitation. */

const THEME_KEY = "hello-beta-dark-mode"; // shared with the ArtistNav landing page
const SIGN_IN_PATH = import.meta.env.DEV ? "/login" : "https://app.artistnav.com/login";

function prefersDark() {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored !== null) return stored === "true";
  } catch {
    // Storage unavailable — fall back to system preference.
  }
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

function attribution() {
  const params = new URLSearchParams(window.location.search);
  const utm = Object.fromEntries(["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]
    .filter((key) => params.has(key)).map((key) => [key, params.get(key)!.slice(0, 160)]));
  return { source: (params.get("source") || "").slice(0, 64) || null, attribution: utm };
}

export default function BetaApplyPage() {
  const id = useId();
  const [dark] = useState(prefersDark);
  const [state, setState] = useState<"idle" | "sending" | "received" | "error">("idle");
  const [error, setError] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");
  const doneRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const previous = document.title;
    document.title = "Apply for the ArtistNav private beta";
    return () => { document.title = previous; };
  }, []);

  useEffect(() => { if (state === "received") doneRef.current?.focus(); }, [state]);

  const busy = state === "sending";
  const field = (name: string) => `${id}-${name}`;

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) ?? "").trim();
    setState("sending"); setError("");
    try {
      const response = await fetch(`${BETA_API}/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: text("full_name"), email: text("email"), organization_name: text("organization_name"),
          industry_role: text("industry_role"), shows_per_month: text("shows_per_month"),
          primary_region: text("primary_region"), primary_interest: text("primary_interest"),
          website: text("website") || null, consent: form.get("consent") === "on",
          nickname: text("nickname"), ...attribution(),
        }),
        signal: AbortSignal.timeout(15000),
      });
      if (!response.ok) {
        throw new Error(response.status === 429 ? "Too many submissions from this network. Please try again in a little while."
          : response.status === 422 ? "Please check your entries — something wasn’t in the expected format."
          : "We couldn’t save your application. Please try again.");
      }
      const data = await response.json();
      if (data.status !== "received") throw new Error("We couldn’t confirm your application. Please try again.");
      setSubmittedEmail(text("email"));
      setState("received");
    } catch (reason) {
      setError(reason instanceof Error && reason.name !== "TimeoutError" ? reason.message : "We couldn’t save your application. Please try again.");
      setState("error");
    }
  }

  return (
    <div className={`hb-root${dark ? " hb-dark" : ""}`}>
      <main className="hb-shell hb-apply">
        <a className="hb-brand hb-apply-brand" href="/" aria-label="ArtistNav home">
          <img src={favicon} alt="" width={34} height={34} />
          <span>ARTISTNAV</span>
        </a>
        {state === "received" ? (
          <section className="hb-apply-card" aria-labelledby={field("done")}>
            <h1 id={field("done")} className="hb-apply-title" tabIndex={-1} ref={doneRef}>Thanks — application received.</h1>
            <p role="status" className="hb-apply-lead">
              We review each application personally. If you’re selected, you’ll get an invitation at <strong>{submittedEmail}</strong>.
            </p>
            <p className="hb-form-alt">Already have an account? <a href={SIGN_IN_PATH}>Sign in</a></p>
          </section>
        ) : (
          <section className="hb-apply-card" aria-labelledby={field("title")}>
            <h1 id={field("title")} className="hb-apply-title">Find Your Next Stage.</h1>
            <p className="hb-apply-lead">
              Discover venues through the touring networks of artists like you. Organize your bookings and build your professional network.
            </p>
            <p className="hb-apply-kicker">Apply for Private Beta Access</p>
            <form className="hb-apply-form" onSubmit={submit}>
              <div className="hb-sr-only" aria-hidden="true">
                <label htmlFor={field("nickname")}>Leave this field empty</label>
                <input id={field("nickname")} name="nickname" tabIndex={-1} autoComplete="off" />
              </div>
              <label htmlFor={field("full_name")}>Full name</label>
              <input id={field("full_name")} name="full_name" autoComplete="name" required maxLength={120} disabled={busy} />
              <label htmlFor={field("email")}>Email address</label>
              <input id={field("email")} name="email" type="email" inputMode="email" autoComplete="email" required maxLength={254} disabled={busy} />
              <label htmlFor={field("organization_name")}>Artist or organization name</label>
              <input id={field("organization_name")} name="organization_name" autoComplete="organization" required maxLength={160} disabled={busy} />
              <label htmlFor={field("industry_role")}>Your relationship to music</label>
              <select id={field("industry_role")} name="industry_role" required defaultValue="" disabled={busy}>
                <option value="" disabled>Choose one</option>
                {INDUSTRY_ROLES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
              <label htmlFor={field("shows_per_month")}>Typical performances per month</label>
              <select id={field("shows_per_month")} name="shows_per_month" required defaultValue="" disabled={busy}>
                <option value="" disabled>Choose one</option>
                {SHOWS_PER_MONTH.map((value) => <option key={value} value={value}>{value.replace("-", "–")}</option>)}
              </select>
              <label htmlFor={field("primary_region")}>Primary city or region</label>
              <input id={field("primary_region")} name="primary_region" autoComplete="address-level2" placeholder="e.g. Nashville, TN" required maxLength={120} disabled={busy} />
              <label htmlFor={field("primary_interest")}>Main reason for joining</label>
              <select id={field("primary_interest")} name="primary_interest" required defaultValue="" disabled={busy}>
                <option value="" disabled>Choose one</option>
                {INTERESTS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
              <label htmlFor={field("website")}>Website or social profile <span className="hb-apply-optional">(optional)</span></label>
              <input id={field("website")} name="website" inputMode="url" autoComplete="url" placeholder="yourband.com or instagram.com/yourband" maxLength={300} disabled={busy} />
              <label className="hb-apply-consent" htmlFor={field("consent")}>
                <input id={field("consent")} name="consent" type="checkbox" required disabled={busy} />
                <span>Email me about ArtistNav beta access.</span>
              </label>
              <p className="hb-apply-privacy" id={field("privacy")}>
                We use this information only to review beta access and contact you about it. We don’t publish it or share it. Applying doesn’t create an account. To have your application deleted, reply to any ArtistNav email.
              </p>
              {error && <p role="alert" className="hb-apply-error">{error}</p>}
              <button className="hb-btn hb-apply-submit" disabled={busy} aria-describedby={field("privacy")}>
                {busy ? "Sending…" : "Apply for beta access"}
              </button>
            </form>
          </section>
        )}
      </main>
    </div>
  );
}
